import { AlertCircle, RotateCcw, Settings, UserRoundSearch } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TabsContent } from '@/components/ui/tabs'
import {
  updateWallState,
  type WallState,
  type WallWinner,
} from '@/lib/firebase/activities'

interface CheckinGuest {
  id: string
  name: string
  pictureUrl?: string
  maritalStatus?: string
}

interface Props {
  wallState: WallState
  filteredCheckins: CheckinGuest[]
  allCheckins: CheckinGuest[]
  localSettings: WallState['settings']
  spinTimerRef: React.RefObject<ReturnType<typeof setTimeout> | null>
}

const handleDismissWinner = () => updateWallState({ winner: null })

export function WallControlTab({
  wallState,
  filteredCheckins,
  allCheckins,
  localSettings,
  spinTimerRef,
}: Props) {
  const [showNoWinnerDialog, setShowNoWinnerDialog] = useState(false)
  const { isSpinning, winner } = wallState
  const maritalFilter = localSettings.maritalStatusFilter ?? []

  const currentSessionId = wallState.currentSessionId || 'default'
  const pastWinnerIds = new Set(
    (wallState.winners || [])
      .filter((w) => w && (w.sessionId || 'default') === currentSessionId)
      .map((w) => w?.id || ''),
  )

  const hasNoPossibleWinner =
    localSettings.excludeWinners &&
    filteredCheckins.length > 0 &&
    filteredCheckins.every((g) => pastWinnerIds.has(g.id))

  const handleDisableExcludeWinners = async () => {
    const newSettings = { ...localSettings, excludeWinners: false }
    const updatedSessions = (wallState.sessions || []).map((s) => {
      if (s.id === currentSessionId) {
        return { ...s, settings: newSettings }
      }
      return s
    })

    try {
      await updateWallState({
        settings: newSettings,
        sessions: updatedSessions,
      })
      toast.success('ปิดการตั้งค่าสุ่มไม่ซ้ำแล้ว')
      setShowNoWinnerDialog(false)
    } catch (err) {
      console.error(err)
      toast.error('เกิดข้อผิดพลาดในการเปลี่ยนการตั้งค่า')
    }
  }

  const handleResetWinners = async () => {
    const filteredWinners = (wallState.winners || []).filter(
      (w): w is WallWinner =>
        w !== null && (w.sessionId || 'default') !== currentSessionId,
    )
    try {
      await updateWallState({ winners: filteredWinners, winner: null })
      toast.info('ล้างรายชื่อผู้ชนะในเซสชันนี้แล้ว')
      setShowNoWinnerDialog(false)
    } catch (err) {
      console.error(err)
      toast.error('เกิดข้อผิดพลาดในการล้างรายชื่อ')
    }
  }

  const handleStartRandomizer = async () => {
    if (hasNoPossibleWinner) {
      setShowNoWinnerDialog(true)
      return
    }

    const pool =
      maritalFilter.length > 0
        ? allCheckins.filter((g) => {
            const m = g.maritalStatus || 'prefer_not_to_say'
            return maritalFilter.includes(m)
          })
        : allCheckins

    if (wallState.isSpinning || pool.length === 0) return

    let available = pool
    if (localSettings.excludeWinners && pastWinnerIds.size > 0) {
      const filtered = pool.filter((g) => !pastWinnerIds.has(g.id))
      available = filtered.length > 0 ? filtered : pool
    }

    const picked = available[Math.floor(Math.random() * available.length)]
    const winner: WallWinner = {
      id: picked.id,
      name: picked.name,
      timestamp: Date.now(),
      sessionId: currentSessionId,
      ...(picked.pictureUrl !== undefined
        ? { pictureUrl: picked.pictureUrl }
        : {}),
    }
    const newWinners = [...(wallState.winners || []), winner]

    if (localSettings.displayMethod === 'bubbles') {
      await updateWallState({ isSpinning: true })
      spinTimerRef.current = setTimeout(async () => {
        spinTimerRef.current = null
        try {
          await updateWallState({
            isSpinning: false,
            winner,
            winners: newWinners,
          })
          toast.success(`ผู้ชนะคือ: ${winner.name}`)
        } catch (err) {
          console.error('Failed to write winner:', err)
          await updateWallState({ isSpinning: false }).catch(console.error)
          toast.error('เกิดข้อผิดพลาดในการสุ่ม')
        }
      }, localSettings.spinDuration * 1000)
    } else {
      await updateWallState({ isSpinning: true, winner, winners: newWinners })
      spinTimerRef.current = setTimeout(async () => {
        spinTimerRef.current = null
        await updateWallState({ isSpinning: false }).catch(console.error)
      }, localSettings.spinDuration * 1000)
    }
  }

  const handleCancelSpin = () => {
    if (spinTimerRef.current) {
      clearTimeout(spinTimerRef.current)
      spinTimerRef.current = null
    }
    updateWallState({ isSpinning: false }).catch(console.error)
  }

  return (
    <TabsContent value="control">
      <Card className="px-4 py-6">
        <CardHeader>
          <CardTitle className="font-bold">แผงควบคุม</CardTitle>
          <CardDescription>สุ่มผู้ชนะจากแขกที่เช็คอินแล้ว</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          {hasNoPossibleWinner && (
            <div className="animate-in fade-in slide-in-from-top-4 flex gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-rose-800 duration-300">
              <AlertCircle className="mt-0.5 size-5 shrink-0 text-rose-500" />
              <div className="space-y-1">
                <h4 className="text-sm font-bold">
                  ไม่สามารถสุ่มผู้ชนะได้ในเซสชันนี้
                </h4>
                <p className="text-xs leading-relaxed text-rose-700">
                  เนื่องจากคุณเปิดการตั้งค่า <strong>"สุ่มไม่ซ้ำคนเดิม"</strong>{' '}
                  และแขกที่เช็คอินทั้งหมดในเงื่อนไขนี้ (
                  {filteredCheckins.length} คน)
                  ได้เป็นผู้ชนะในเซสชันนี้ไปหมดแล้ว
                </p>
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowNoWinnerDialog(true)}
                    className="h-8 rounded-lg border-rose-200 bg-white text-xs font-bold text-rose-700 hover:bg-rose-100 hover:text-rose-800"
                  >
                    ดูทางเลือกแก้ไข
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-2">
            {/* Left: Start button */}
            <div className="space-y-4">
              <div className="text-muted-foreground text-sm">
                แขกเช็คอินแล้ว:{' '}
                <span className="text-primary font-bold">
                  {filteredCheckins.length} คน
                  {maritalFilter.length > 0 && (
                    <span className="text-muted-foreground ml-1 font-normal">
                      (จาก {allCheckins.length} คน)
                    </span>
                  )}
                </span>
              </div>
              <Button
                size="lg"
                disabled={!isSpinning && filteredCheckins.length === 0}
                onClick={isSpinning ? handleCancelSpin : handleStartRandomizer}
                className={`h-12 w-full rounded-xl px-6 text-lg font-bold shadow-lg transition-all active:scale-95 disabled:opacity-50 ${
                  isSpinning
                    ? 'bg-rose-100 text-rose-600 shadow-rose-100/50 hover:bg-rose-200'
                    : 'bg-primary hover:bg-primary/90 shadow-primary/20'
                }`}
              >
                <UserRoundSearch className="mr-2 size-5" />
                {isSpinning ? 'กำลังสุ่ม... (กดเพื่อยกเลิก)' : 'สุ่มผู้ชนะ'}
              </Button>
            </div>
            {/* Right: Status */}
            <div className="bg-muted/50 border-muted-foreground/20 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8">
              <div className="text-muted-foreground mb-2 text-sm font-medium tracking-wider uppercase">
                สถานะ
              </div>
              <div
                className={`text-3xl font-bold ${isSpinning ? 'text-rose-500' : winner ? 'text-green-500' : 'text-muted-foreground'}`}
              >
                {isSpinning
                  ? 'กำลังสุ่ม...'
                  : winner
                    ? 'มีผู้ชนะ!'
                    : 'รอดำเนินการ'}
              </div>
            </div>
          </div>

          {/* Current winner card */}
          {winner && !isSpinning && (
            <div className="bg-primary/10 border-primary/20 animate-in fade-in zoom-in rounded-2xl border p-6 duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="size-14 border-2 border-rose-300">
                    <AvatarImage src={winner.pictureUrl} />
                    <AvatarFallback className="bg-rose-50 text-rose-500 uppercase">
                      {winner.name.slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ผู้ชนะ
                    </div>
                    <div className="text-primary text-2xl font-bold">
                      {winner.name}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissWinner}
                  className="text-muted-foreground hover:text-destructive"
                >
                  ปิด
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={showNoWinnerDialog}
        onOpenChange={setShowNoWinnerDialog}
      >
        <DialogContent className="wedding-card p-6 sm:max-w-112.5">
          <DialogHeader className="flex flex-col items-center text-center">
            <div className="mb-2 flex size-12 items-center justify-center rounded-full bg-rose-50 text-rose-500">
              <AlertCircle className="size-6" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900">
              ไม่มีผู้มีสิทธิ์ลุ้นรางวัลเหลืออยู่
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm text-gray-500">
              แขกเช็คอินทั้งหมดในเงื่อนไขการสุ่ม ({filteredCheckins.length} คน)
              ได้รับรางวัลในเซสชัน{' '}
              <strong>
                "
                {wallState.sessions?.find((s) => s.id === currentSessionId)
                  ?.name || 'เซสชันปัจจุบัน'}
                "
              </strong>{' '}
              นี้ไปหมดแล้ว
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="text-xs font-bold tracking-wider text-gray-400 uppercase">
              ทางเลือกในการจัดการ
            </div>

            {/* Option 1: Disable excludeWinners */}
            <button
              type="button"
              className="flex w-full cursor-pointer items-start gap-3 rounded-xl border border-gray-100 p-4 text-left transition-colors hover:bg-gray-50"
              onClick={handleDisableExcludeWinners}
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <Settings className="size-4" />
              </div>
              <div className="space-y-0.5">
                <div className="text-sm font-bold text-gray-900">
                  ปิดการตั้งค่า "สุ่มไม่ซ้ำคนเดิม"
                </div>
                <div className="text-xs text-gray-500">
                  อนุญาตให้แขกที่เคยได้รางวัลแล้วสามารถลุ้นรับรางวัลได้อีกครั้ง
                </div>
              </div>
            </button>

            {/* Option 2: Reset Winners of this session */}
            <button
              type="button"
              className="flex w-full cursor-pointer items-start gap-3 rounded-xl border border-gray-100 p-4 text-left transition-colors hover:border-rose-100 hover:bg-rose-50/50"
              onClick={handleResetWinners}
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500">
                <RotateCcw className="size-4" />
              </div>
              <div className="space-y-0.5">
                <div className="text-sm font-bold text-rose-700">
                  ล้างรายชื่อผู้ชนะของเซสชันนี้
                </div>
                <div className="text-xs text-rose-600/80">
                  รีเซ็ตผู้ชนะทั้งหมดในเซสชันนี้ ({pastWinnerIds.size} คน)
                  เพื่อเริ่มสุ่มใหม่จากแขกทุกคน
                </div>
              </div>
            </button>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              className="w-full rounded-xl"
              onClick={() => setShowNoWinnerDialog(false)}
            >
              ปิด
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  )
}
